import { useNavigate, useParams, useSearch } from "@tanstack/react-router";

import { carryFilters } from "@/lib/filters";

/** The router search param that scopes the Change Log to one keyword. */
export const ITEM_SCOPE_PARAM = "f_item";

/**
 * Open another of this module's own tabs.
 *
 * The shell owns the tab bar, and a native module cannot reach it — but it can
 * ask the router for the same navigation the bar performs, which is what
 * `ShellPage`'s `onOpenTab` does. That matters for one real case: a proposal on
 * the Home tab whose approval carries profit risk must be decided where the
 * full impact is visible, so the row sends the reader to the Review queue
 * instead of approving blind.
 *
 * The area and module keys come from the ROUTE rather than from a constant
 * here, so the module never asserts where it is mounted.
 *
 * `search` adds params to the ones a tab switch carries, so another tab can hand
 * over a scope through the router, which owns the URL.
 */
export function useOpenMachineTab(): (tabKey: string, search?: Record<string, string>) => void {
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as { area?: string; module?: string };
  return (tabKey: string, search?: Record<string, string>) => {
    if (!params.area || !params.module) return;
    void navigate({
      to: "/app/$area/$module/$tab",
      params: { area: params.area, module: params.module, tab: tabKey },
      ...(search
        ? { search: ((prev: Record<string, unknown>) => ({ ...carryFilters(prev), ...search })) as never }
        : {}),
    });
  };
}

/**
 * The scoped item in a search object. The router parses a numeric-looking value
 * into a number, so a number or boolean is read back as text; anything else is
 * no scope.
 */
export function itemScopeFromSearch(search: Record<string, unknown>): string | undefined {
  const value = search[ITEM_SCOPE_PARAM];
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return typeof value === "string" && value !== "" ? value : undefined;
}

/** The same search object with the item scope removed. */
export function withoutItemScope(search: Record<string, unknown>): Record<string, unknown> {
  const rest = { ...search };
  delete rest[ITEM_SCOPE_PARAM];
  return rest;
}

/** The Change Log's item scope, and the way to clear it. */
export function useChangeLogItemScope(): { item: string | undefined; clearItem: () => void } {
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as { area?: string; module?: string; tab?: string };
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  return {
    item: itemScopeFromSearch(search),
    clearItem: () => {
      if (!params.area || !params.module || !params.tab) return;
      void navigate({
        to: "/app/$area/$module/$tab",
        params: { area: params.area, module: params.module, tab: params.tab },
        search: ((prev: Record<string, unknown>) => withoutItemScope(prev)) as never,
      });
    },
  };
}
